import {useEffect, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {FormattedMessage, useIntl} from 'react-intl'
import {
  Breadcrumb,
  Button,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
  Card,
} from 'react-bootstrap'
//import {AsideMenucommissionReports} from './components/AsideMenucommissionReports'
import {useLazyQuery, useQuery, useMutation} from '@apollo/client'
import {
  GET_COMMISSION_REPORT_PAYROLL,
  GET_COMMISSION_REPORT_PAYROLL_BY_ADVISOR,
  GET_COMMISSION_REPORT_PAYROLL_BY_AGENT_AND_ADVISOR,
} from '../../gql/queries/commissionReportsDetailsApiQueries'
import {GET_COMMISSION_REPORT_BY_ID_API} from '../../gql/queries/commissionReportsApiQueries'
import {useGetUser} from '../../store/userStore'
import {URL} from '../../gql/client'

import {toast} from 'react-toastify'
import {
  IResCommissionReportPayroll,
  IAgentAdvisorPayroll,
} from '../../types/commissionReportDetailApi'
import {hasExactPermission, hasPermission, parseId, parsePrice} from '../../helpers'
import moment from 'moment'


import {ModalEditCommissionReport} from '../commissionReports/components/ModalEditCommissionReport'
import {NodeCommissionReport} from '../../types/commissionReportApi'
import {TOGGLE_STATUS_COMMISSION_REPORT} from '../../gql/mutations/commissionReportMutations'
import {ModalConfirmation} from '../commissionReports/components/ModalConfirmation'
import {ALL_PERMISSIONS} from '../../helpers/consts'
import { GET_ALL_USERS_FILTER } from '../../gql/queries/usersQuery'
import { IResAllUsers } from '../oneUser/oneUserTypes'
import {PhoneProviderActions} from '../../components/telephony/PhoneProviderActions'

export const CommissionReportsPayrollPage = () => {
  const intl = useIntl()
  const dash = '-'
  const emDash = '—'
  const space = ' '
  const permissions = useGetUser((s) => s.user?.permissions)
  const canEditCommission =
    permissions && hasExactPermission(permissions, ALL_PERMISSIONS.EDIT_COMMISSION)
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const currentUserId = useGetUser((s) => s.user?.user?.id)
  const isAgente = userGroups.includes('Agente')

  // --- React Router
  const {id} = useParams<{id: string}>()
  const navigate = useNavigate()

  // --- Estado para el ID y nombre del reporte
  const [reportId, setReportId] = useState<string | null>(null)

  // --- GraphQL Hooks
  const [getOneCommissionReport, {data: commissionReportById}] = useLazyQuery(
    GET_COMMISSION_REPORT_BY_ID_API,
    {
      fetchPolicy: 'no-cache',
    }
  )

  const [
    gqlGetCommissionReportPayroll,
    {data: dataCommissionReportPayroll, loading, error, refetch: refetchEntries},
  ] = useLazyQuery<IResCommissionReportPayroll>(GET_COMMISSION_REPORT_PAYROLL, {
    fetchPolicy: 'no-cache',
  })

  const [
    gqlGetPayrollByAdvisor,
    {data: dataPayrollByAdvisor, loading: loadingAdvisor},
  ] = useLazyQuery<IResCommissionReportPayroll>(GET_COMMISSION_REPORT_PAYROLL_BY_ADVISOR, {
    fetchPolicy: 'no-cache',
  })

  const [
    gqlGetPayrollByAgentAndAdvisor,
    {data: dataPayrollByAgentAndAdvisor, loading: loadingAgentAdvisor},
  ] = useLazyQuery<IResCommissionReportPayroll>(GET_COMMISSION_REPORT_PAYROLL_BY_AGENT_AND_ADVISOR, {
    fetchPolicy: 'no-cache',
  })

  const [agentAdvisorData, setAgentAdvisorData] = useState<IAgentAdvisorPayroll[]>([])

  const {data: dataMembers} = useQuery<IResAllUsers>(
    GET_ALL_USERS_FILTER,
    {
      fetchPolicy: 'no-cache',
      variables: {requireNpn: true},
    }
  )

  // --- Modales y UI
  const [isOpenEdit, setIsOpenEdit] = useState(false)
  const [infoEdit, setInfoEdit] = useState<NodeCommissionReport | null>(null)

  // -- Reopen and close commission report for toggling status
  const [gqlToggleStatusCommissionReport] = useMutation(TOGGLE_STATUS_COMMISSION_REPORT)
  const [isOpenToggleModal, setIsOpenToggleModal] = useState(false)
  const [toggleAction, setToggleAction] = useState<'close' | 'reopen'>('close')

  const handleToggleStatus = async () => {
    if (!report?.id) return

    try {
      await gqlToggleStatusCommissionReport({
        variables: {
          id: parseId(report.id),
          newStatus: report.status === 'DRAFT' ? 'COMPLETED' : 'DRAFT',
        },
      })
      toast.success(
        report.status === 'DRAFT'
          ? intl.formatMessage({id: 'commissions.reports.toast.closed', defaultMessage: 'Commission Report closed successfully'})
          : intl.formatMessage({id: 'commissions.reports.toast.reopened', defaultMessage: 'Commission Report reopened successfully'})
      )
      await refetchReport()
      await refetchEntries()
    } catch (error: any) {
      toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message: error.message}))
    } finally {
      setIsOpenToggleModal(false)
    }
  }

  // --- Filtros y paginación
  const [tabFilters] = useState('CommissionReportsDetails')
  const [valueUser, setValueUser] = useState(isAgente && currentUserId ? currentUserId : '')
  const [viewMode, setViewMode] = useState<'agent' | 'agent_advisor' | 'advisor'>('agent')

  type IFilterType = 'all' | 'COMMISSION' | 'DEDUCTION' | 'CHARGEBACK'

  const [filterType, setFilterType] = useState<IFilterType>('all')

  // --- Datos del detalle
  const [dataArr, setDataArr] = useState<IResCommissionReportPayroll['commissionReportPayroll']>([])

  const initialDetailData = {client: '', status: '', user: ''}
  const [detailData, setDetailData] = useState(initialDetailData)

  // --- Efecto para cargar reporte al inicio
  useEffect(() => {
    const decodedId = id ? String(parseId(id)) : null
    if (decodedId) {
      setReportId(decodedId)
      getOneCommissionReport({variables: {id: decodedId}})
    }
  }, [id, getOneCommissionReport])

  // --- Función para recargar reporte individual
  const refetchReport = async () => {
    if (reportId) {
      await getOneCommissionReport({
        variables: {id: reportId},
        fetchPolicy: 'no-cache',
      })
    }
  }

  // --- Obtener detalles del reporte con filtros
  const handleFilters = () => {
    const commissionReportId = id ? parseId(id) : null
    const userId = valueUser ? parseId(valueUser) : null

    const baseVars = {
      client: detailData.client || null,
      status: 'APPROVED',
      commissionType: filterType === 'all' ? null : filterType,
      commissionReport: commissionReportId,
    }

    if (viewMode === 'advisor') {
      gqlGetPayrollByAdvisor({ variables: baseVars })
    } else if (viewMode === 'agent_advisor') {
      gqlGetPayrollByAgentAndAdvisor({ variables: baseVars })
    } else {
      gqlGetCommissionReportPayroll({ variables: { ...baseVars, user: userId } })
    }
  }

  useEffect(() => {
    handleFilters()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gqlGetCommissionReportPayroll, gqlGetPayrollByAdvisor, gqlGetPayrollByAgentAndAdvisor, tabFilters, viewMode])

  // --- Cargar datos de detalles y Validacion de totales
  const [totals, setTotals] = useState({
    approved: 0,
  })

  useEffect(() => {
    if (viewMode === 'agent_advisor') {
      const source = dataPayrollByAgentAndAdvisor?.commissionReportPayrollByAgentAndAdvisor
      if (!source) return
      setAgentAdvisorData(source as IAgentAdvisorPayroll[])
      setTotals({approved: source.reduce((acc, item) => acc + Number(item.totalAmount), 0)})
    } else {
      const source =
        viewMode === 'advisor'
          ? dataPayrollByAdvisor?.commissionReportPayrollByAdvisor
          : dataCommissionReportPayroll?.commissionReportPayroll
      if (!source) return
      setDataArr(source)
      setTotals({approved: source.reduce((acc, item) => acc + parseFloat(item.totalAmount), 0)})
    }
  }, [dataCommissionReportPayroll, dataPayrollByAdvisor, dataPayrollByAgentAndAdvisor, viewMode])

  // --- Manejo de errores
  useEffect(() => {
    if (error) toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message: error.message}))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  const handleClientName = () => {
    handleFilters()
  }

  const user = useGetUser((s) => s.user)
  const [loadingExports, setLoadingExport] = useState(false)

  const safeParseId = (value: any): number | null => {
    const id = parseId(value) as number
    return !id || isNaN(id) || id === 0 ? null : id
  }

  const _downloadExcel = async (endpoint: string, filename: string) => {
    if (!user) return
    const {token} = user
    if (!token) return
    const myHeaders = new Headers()
    myHeaders.append('Authorization', `Bearer ${token}`)
    const params = new URLSearchParams()
    const reportNumId = safeParseId(report.id)?.toString()
    if (reportNumId) params.append('id', reportNumId)
    setLoadingExport(true)
    try {
      const res = await fetch(`${URL}/${endpoint}?${params.toString()}`, {method: 'GET', headers: myHeaders})
      if (!res.ok) throw new Error(intl.formatMessage({id: 'commissions.logs.exportError', defaultMessage: 'Error exporting'}))
      const blob = await res.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
    } catch (err) {
      toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message: (err as {message: string}).message}))
    } finally {
      setLoadingExport(false)
    }
  }

  const handleDownload = () => {
    if (viewMode === 'advisor') {
      _downloadExcel(
        'export-commission-payroll-by-advisor-excel',
        `${report.name} - payroll by advisor - ${moment().format('YY-MM-DD')}.xlsx`
      )
    } else if (viewMode === 'agent_advisor') {
      _downloadExcel(
        'export-commission-payroll-by-agent-and-advisor-excel',
        `${report.name} - payroll agent + advisor - ${moment().format('YY-MM-DD')}.xlsx`
      )
    } else {
      _downloadExcel(
        'export-commission-payroll-excel',
        `${report.name} - payroll - ${moment().format('YY-MM-DD')}.xlsx`
      )
    }
  }

  // --- Validación de carga
  if (!commissionReportById || !commissionReportById.commissionReportById) {
    return (
      <div className='d-flex flex-center' style={{height: '30vh'}}>
        <Spinner animation='border' />
      </div>
    )
  }

  // --- Report listo
  const report = commissionReportById.commissionReportById

  // --- Render principal (return) se mantiene igual ---
  return (
    <div className='policies-page'>
      <header className='header'>
        <Breadcrumb>
          <Breadcrumb.Item className='fs-3' onClick={(e) => navigate('/commissions')}>
            <FormattedMessage id='commissions.reports.payroll.breadcrumbReports' defaultMessage='Reports List' />
          </Breadcrumb.Item>
          <Breadcrumb.Item className='fs-3'><FormattedMessage id='commissions.reports.payroll' defaultMessage='Payroll' /></Breadcrumb.Item>
        </Breadcrumb>
        <div className='policies-summary summary-cards mb-3'>
          <Card className='card-summary green flex-fill mx-2 text-center'>
            <Card.Title><FormattedMessage id='commissions.detail.payroll.approvedTotal' defaultMessage='Approved Total Amount' /></Card.Title>
            <Card.Body>
              {loading && <Spinner animation='border' />}
              {parsePrice(totals.approved ?? 0)}
            </Card.Body>
          </Card>
        </div>
      </header>
      <Row className='commissionReports'>
        <Col className='container'>
          <div className='policies-container'>
            <div className='title-header'>
              <h3>
                <FormattedMessage id='commissions.logs.reportName' defaultMessage='Report: {name}' values={{name: report.name}} />{space}
                {canEditCommission && (
                  <span
                    style={{cursor: 'pointer'}}
                    className='text-primary ms-2'
                    onClick={() => {
                      setInfoEdit(report)
                      setIsOpenEdit(true)
                    }}
                  >
                    <i className='bi bi-pencil-square' /> <FormattedMessage id='common.action.edit' defaultMessage='Edit' />
                  </span>
                )}
                <br />
                <small>
                  <FormattedMessage id='commissions.logs.date' defaultMessage='Date:' />{space}
                  {report.effectiveMonth && report.effectiveYear
                    ? moment(
                        `${report.effectiveYear}-${report.effectiveMonth}-01`,
                        'YYYY-MM-DD'
                      ).format('MMMM YYYY')
                    : ''}
                </small>
              </h3>

              <div className='d-flex gap-2 align-items-center'>
                <Button size='sm' onClick={() => navigate(`/commissions/${report.id}/details`)}>
                  <i className='bi bi-list-check' /> <FormattedMessage id='commissions.detail.payroll.seeDetails' defaultMessage='See details' />
                </Button>

                <div className='btn-group btn-group-sm' role='group'>
                  <Button
                    size='sm'
                    variant={viewMode === 'agent' ? 'primary' : 'outline-primary'}
                    onClick={() => setViewMode('agent')}
                  >
                    <i className='bi bi-person' /> <FormattedMessage id='commissions.detail.payroll.byAgent' defaultMessage='By Agent' />
                  </Button>
                  <Button
                    size='sm'
                    variant={viewMode === 'agent_advisor' ? 'primary' : 'outline-primary'}
                    onClick={() => setViewMode('agent_advisor')}
                  >
                    <i className='bi bi-people' /> <FormattedMessage id='commissions.detail.payroll.byAgentAdvisor' defaultMessage='By Agent + Advisor' />
                  </Button>
                  <Button
                    size='sm'
                    variant={viewMode === 'advisor' ? 'primary' : 'outline-primary'}
                    onClick={() => setViewMode('advisor')}
                  >
                    <i className='bi bi-person-gear' /> <FormattedMessage id='commissions.detail.payroll.byAdvisor' defaultMessage='By Advisor' />
                  </Button>
                </div>

                {canEditCommission && (
                  <>
                    <Button disabled={loadingExports} size='sm' onClick={handleDownload}>
                      {loadingExports ? (
                        <>
                          <Spinner animation='border' size='sm' />{' '}
                        </>
                      ) : (
                        <i className='bi bi-download'></i>
                      )}
                      <FormattedMessage id='commissions.logs.export' defaultMessage='Export' />
                    </Button>
                    <Button
                      size='sm'
                      variant={report.status === 'DRAFT' ? 'danger' : 'success'}
                      className='ms-2'
                      onClick={() => {
                        setToggleAction(report.status === 'DRAFT' ? 'close' : 'reopen')
                        setIsOpenToggleModal(true)
                      }}
                    >
                      {report.status === 'DRAFT' ? (
                        <>
                          <i className='bi bi-lock-fill' /> <FormattedMessage id='commissions.detail.payroll.finishClose' defaultMessage='Finish and Close Report' />
                        </>
                      ) : (
                        <>
                          <i className='bi bi-unlock-fill' /> <FormattedMessage id='commissions.reports.reopen.title' defaultMessage='Reopen Report' />
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div className='filters-header'>
              <InputGroup>
                <Form.Control
                  placeholder={intl.formatMessage({id: 'commissions.detail.payroll.filterClientPlaceholder', defaultMessage: 'Filter by client name'})}
                  aria-label={intl.formatMessage({id: 'commissions.detail.payroll.filterClientAria', defaultMessage: "Client's name with two button addons"})}
                  onChange={(e) =>
                    setDetailData((prev) => ({
                      ...prev,
                      client: e.target.value,
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleClientName()
                    }
                  }}
                  value={detailData.client}
                  disabled={false}
                />

                <Form.Select
                  value={filterType}
                  aria-label={intl.formatMessage({id: 'commissions.detail.payroll.filterByType', defaultMessage: 'Filter by Type'})}
                  onChange={(e) => setFilterType(e.target.value as IFilterType)}
                  className='ms-2'
                >
                  <option value='all'>{intl.formatMessage({id: 'commissions.detail.payroll.filterByType', defaultMessage: 'Filter by Type'})}</option>
                  <option value='COMMISSION'>{intl.formatMessage({id: 'commissions.detail.commissionType.commission', defaultMessage: 'Commission'})}</option>
                  <option value='DEDUCTION'>{intl.formatMessage({id: 'commissions.detail.commissionType.deduction', defaultMessage: 'Deduction'})}</option>
                  <option value='CHARGEBACK'>{intl.formatMessage({id: 'commissions.detail.commissionType.chargeback', defaultMessage: 'Chargeback'})}</option>
                </Form.Select>

                {viewMode === 'agent' && !isAgente && (
                  <Form.Select
                    aria-label={intl.formatMessage({id: 'commissions.reports.payroll.filterByAgent', defaultMessage: 'Filter by Agent'})}
                    name='userFilter'
                    className='ms-2'
                    value={valueUser}
                    onChange={(e) => {
                      const {value} = e.target
                      setValueUser(value)
                    }}
                    disabled={isAgente}
                  >
                    <option value=''>{intl.formatMessage({id: 'commissions.reports.payroll.filterByAgent', defaultMessage: 'Filter by Agent'})}</option>
                    {dataMembers?.allUsers.edges.map(({node}) => (
                      <option key={node.id} value={node.id}>
                        {node.firstName} {node.lastName} {dash} {node.email}
                        {node.npn ? intl.formatMessage({id: 'commissions.reports.payroll.npnSuffix', defaultMessage: ' ({npn})'}, {npn: node.npn}) : ''}
                      </option>
                    ))}
                  </Form.Select>
                )}
                <Button
                  disabled={false}
                  onClick={() => handleFilters()}
                  variant='outline-success'
                  style={{border: '1px solid #dbdfe9'}}
                  className='ms-2'
                >
                  <FormattedMessage id='common.action.search' defaultMessage='Search' />
                </Button>
              </InputGroup>
            </div>
            <div className='results-header'>
              <div className='col-md-3'>
                <div className='results-filter'>
                  <Col lg={12} md={12} sm={12} xs={12}></Col>
                </div>
              </div>
            </div>

            <div className='table-responsive table table-striped table-hover '>
              <table
                className='table-scrollable table-scroll  table table-striped table-hover '
                style={{whiteSpace: 'nowrap'}}
              >
                {/* ── By Agent ── */}
                {viewMode === 'agent' && (
                  <>
                    <thead>
                      <tr role='row'>
                        <th className='fw-bold'><FormattedMessage id='common.field.agent' defaultMessage='Agent' /></th>
                        <th className='fw-bold'><FormattedMessage id='common.field.email' defaultMessage='Email' /></th>
                        <th className='fw-bold'><FormattedMessage id='commissions.detail.payroll.npn' defaultMessage='NPN' /></th>
                        <th className='fw-bold'><FormattedMessage id='commissions.detail.payroll.cellphone' defaultMessage='Cellphone' /></th>
                        <th className='fw-bold'><FormattedMessage id='commissions.detail.payroll.totalAmount' defaultMessage='Total Amount' /></th>
                      </tr>
                    </thead>
                    {loading || !dataCommissionReportPayroll ? (
                      <tbody><tr><td colSpan={5}><div className='d-flex flex-center' style={{height: '30vh'}}><Spinner animation='border' /></div></td></tr></tbody>
                    ) : (
                      <tbody>
                        {dataArr.length === 0 ? (
                          <tr><td colSpan={5}><div className='d-flex flex-center' style={{height: '30vh'}}><FormattedMessage id='commissions.detail.payroll.emptyAgent' defaultMessage='There are no commission payroll records available' /></div></td></tr>
                        ) : (
                          dataArr.map((item, index) => (
                            <tr key={item.user.id || index}>
                              <td>{item.user.firstName} {item.user.lastName}</td>
                              <td>{item.user.email}</td>
                              <td>{item.user.npn}</td>
                              <td><PhoneProviderActions phone={item.user.cellphone} /></td>
                              <td>{parsePrice(item.totalAmount)}</td>
                            </tr>
                          ))
                        )}
                        <tr>
                          <td colSpan={4} style={{textAlign: 'right', fontWeight: 'bold'}}><FormattedMessage id='commissions.detail.payroll.grandTotal' defaultMessage='Grand Total:' /></td>
                          <td style={{fontWeight: 'bold'}}>{parsePrice(totals.approved)}</td>
                        </tr>
                      </tbody>
                    )}
                  </>
                )}

                {/* ── By Agent + Advisor ── */}
                {viewMode === 'agent_advisor' && (
                  <>
                    <thead>
                      <tr role='row'>
                        <th className='fw-bold'><FormattedMessage id='commissions.detail.payroll.agentAdvisor' defaultMessage='Agent / Advisor' /></th>
                        <th className='fw-bold'><FormattedMessage id='common.field.email' defaultMessage='Email' /></th>
                        <th className='fw-bold'><FormattedMessage id='commissions.detail.payroll.npn' defaultMessage='NPN' /></th>
                        <th className='fw-bold'><FormattedMessage id='commissions.detail.payroll.cellphone' defaultMessage='Cellphone' /></th>
                        <th className='fw-bold'><FormattedMessage id='commissions.detail.payroll.amount' defaultMessage='Amount' /></th>
                      </tr>
                    </thead>
                    {loadingAgentAdvisor || !dataPayrollByAgentAndAdvisor ? (
                      <tbody><tr><td colSpan={5}><div className='d-flex flex-center' style={{height: '30vh'}}><Spinner animation='border' /></div></td></tr></tbody>
                    ) : (
                      <tbody>
                        {agentAdvisorData.length === 0 ? (
                          <tr><td colSpan={5}><div className='d-flex flex-center' style={{height: '30vh'}}><FormattedMessage id='commissions.detail.payroll.emptyRecords' defaultMessage='There are no records available' /></div></td></tr>
                        ) : (
                          agentAdvisorData.map((entry, i) => (
                            <>
                              {/* Agent row */}
                              <tr key={`agent-${entry.agent.id || i}`} style={{backgroundColor: '#f1f5fb'}}>
                                <td style={{fontWeight: 700}}>
                                  <i className='bi bi-person-fill me-1 text-primary' />
                                  {entry.agent.firstName} {entry.agent.lastName}
                                </td>
                                <td style={{fontWeight: 700}}>{entry.agent.email}</td>
                                <td style={{fontWeight: 700}}>{entry.agent.npn}</td>
                                <td><PhoneProviderActions phone={entry.agent.cellphone} /></td>
                                <td style={{fontWeight: 700}}>{parsePrice(entry.totalAmount)}</td>
                              </tr>
                              {/* Advisor sub-rows — solo si hay asesores asignados */}
                              {entry.advisors.map((adv, j) => (
                                <tr key={`adv-${i}-${j}`}>
                                  <td style={{paddingLeft: '2rem', color: '#555'}}>
                                    <i className='bi bi-person-gear me-1 text-muted' />
                                    {adv.advisor
                                      ? adv.advisor.firstName + space + adv.advisor.lastName
                                      : emDash}
                                  </td>
                                  <td style={{color: '#555'}}>{adv.advisor?.email || ''}</td>
                                  <td />
                                  <td />
                                  <td>{parsePrice(adv.amount)}</td>
                                </tr>
                              ))}
                            </>
                          ))
                        )}
                        <tr>
                          <td colSpan={4} style={{textAlign: 'right', fontWeight: 'bold'}}><FormattedMessage id='commissions.detail.payroll.grandTotal' defaultMessage='Grand Total:' /></td>
                          <td style={{fontWeight: 'bold'}}>{parsePrice(totals.approved)}</td>
                        </tr>
                      </tbody>
                    )}
                  </>
                )}

                {/* ── By Advisor ── */}
                {viewMode === 'advisor' && (
                  <>
                    <thead>
                      <tr role='row'>
                        <th className='fw-bold'><FormattedMessage id='commissions.detail.payroll.advisor' defaultMessage='Advisor' /></th>
                        <th className='fw-bold'><FormattedMessage id='common.field.email' defaultMessage='Email' /></th>
                        <th className='fw-bold'><FormattedMessage id='commissions.detail.payroll.totalAmount' defaultMessage='Total Amount' /></th>
                      </tr>
                    </thead>
                    {loadingAdvisor || !dataPayrollByAdvisor ? (
                      <tbody><tr><td colSpan={3}><div className='d-flex flex-center' style={{height: '30vh'}}><Spinner animation='border' /></div></td></tr></tbody>
                    ) : (
                      <tbody>
                        {dataArr.length === 0 ? (
                          <tr><td colSpan={3}><div className='d-flex flex-center' style={{height: '30vh'}}><FormattedMessage id='commissions.detail.payroll.emptyRecords' defaultMessage='There are no records available' /></div></td></tr>
                        ) : (
                          dataArr.map((item, index) => (
                            <tr key={item.user.id || index}>
                              <td>{item.user.firstName} {item.user.lastName}</td>
                              <td>{item.user.email}</td>
                              <td>{parsePrice(item.totalAmount)}</td>
                            </tr>
                          ))
                        )}
                        <tr>
                          <td colSpan={2} style={{textAlign: 'right', fontWeight: 'bold'}}><FormattedMessage id='commissions.detail.payroll.grandTotal' defaultMessage='Grand Total:' /></td>
                          <td style={{fontWeight: 'bold'}}>{parsePrice(totals.approved)}</td>
                        </tr>
                      </tbody>
                    )}
                  </>
                )}
              </table>
            </div>
          </div>
        </Col>
      </Row>

      {/*
      showAside && (
        <AsideMenucommissionReports
          isOpen={showAside}
          onClose={() => setShowAside(false)}
          tabFilters={tabFilters}
          setTabFilters={setTabFilters}
        />
      )
      */}

      {isOpenEdit && infoEdit && (
        <ModalEditCommissionReport
          isOpen={isOpenEdit}
          onClose={() => {
            setIsOpenEdit(false)
          }}
          infoEdit={infoEdit}
          refetch={refetchReport}
        />
      )}

      {isOpenToggleModal && (
        <ModalConfirmation
          isOpen={isOpenToggleModal}
          onClose={() => setIsOpenToggleModal(false)}
          onConfirm={handleToggleStatus}
          refetch={refetchEntries}
          title={toggleAction === 'close'
            ? intl.formatMessage({id: 'commissions.reports.close.title', defaultMessage: 'Close Report'})
            : intl.formatMessage({id: 'commissions.reports.reopen.title', defaultMessage: 'Reopen Report'})}
          confirmText={toggleAction === 'close'
            ? intl.formatMessage({id: 'commissions.reports.close.confirm', defaultMessage: 'Yes, Close Report'})
            : intl.formatMessage({id: 'commissions.reports.reopen.confirm', defaultMessage: 'Yes, Reopen Report'})}
          confirmVariant={toggleAction === 'close' ? 'danger' : 'success'}
          body={
            toggleAction === 'close' ? (
              <>
                <p>
                  <strong><FormattedMessage id='commissions.reports.close.q' defaultMessage='Are you sure you wish to close this report?' /></strong>
                </p>
                <ul>
                  <li>
                    <FormattedMessage
                      id='commissions.detail.payroll.closeLi1'
                      defaultMessage='All <b>PENDING</b> policies will be marked as <b>NOT ASSIGNED</b>.'
                      values={{b: (chunks: React.ReactNode) => <strong>{chunks}</strong>}}
                    />
                  </li>
                  <li><FormattedMessage id='commissions.detail.payroll.closeLi2' defaultMessage='You confirm the rates and assignments are final.' /></li>
                </ul>
              </>
            ) : (
              <p>
                <FormattedMessage
                  id='commissions.detail.payroll.reopenBody'
                  defaultMessage='<b>Are you sure you want to reopen this report?</b><br></br>All <b>NOT ASSIGNED</b> policies will be marked as <b>PENDING</b>.'
                  values={{
                    b: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
                    br: () => <br />,
                  }}
                />
              </p>
            )
          }
        />
      )}
    </div>
  )
}
